import { useState, useEffect, useRef } from 'react';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Pencil, Check, X, CheckCircle } from 'lucide-react';
import { trackGTMEvent, trackGTMFormEngagement } from './GoogleTagManager';
import { trackEvent as trackEventHj, trackFormEngagementHj } from './Hotjar';
import { trackEventMp, trackFormEngagementMp } from './Mixpanel';
import { useTranslation } from 'react-i18next';

interface EditableEmailProps {
  email: string;
  onEmailChange: (newEmail: string) => void;
  source: 'primary_cta' | 'newsletter_form' | 'funnel_interests';
}

export const EditableEmail = ({ email, onEmailChange, source }: EditableEmailProps) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedEmail, setEditedEmail] = useState(email);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const emailSchema = z.string().email({ message: t('emailEdit.invalidEmail') });

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    try {
      emailSchema.parse(editedEmail);
      onEmailChange(editedEmail.trim());
      setIsEditing(false);
      setError(null);
      
      trackGTMEvent('email_edited_in_modal', {
        event_category: 'form_interaction',
        source,
        previous_email: email,
        new_email: editedEmail.trim()
      });
      trackGTMFormEngagement(`${source}_modal`, 'email_edited', 'email');
      trackEventMp('email_edited_in_modal', { source });
      trackEventHj('email_edited_in_modal');
      trackFormEngagementHj(`${source}_modal`, 'email_edited');
      trackFormEngagementMp(`${source}_modal`, 'email_edited');
    } catch (err) {
      if (err instanceof z.ZodError) {
        setError(err.errors[0].message);
      }
    }
  };

  const handleCancel = () => {
    setEditedEmail(email);
    setIsEditing(false);
    setError(null);
  };

  if (isEditing) {
    return (
      <div className="mb-4 p-3 bg-muted/30 rounded-lg border-2 border-newsletter-primary/30 space-y-2">
        <div className="flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
          <p className="text-xs text-muted-foreground">{t('emailEdit.editing')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            type="email"
            value={editedEmail}
            onChange={(e) => setEditedEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') handleCancel();
            }}
            className="h-9 text-sm flex-1"
          />
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            className="h-9 px-3"
            aria-label={t('emailEdit.saveButton')}
          >
            <Check className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={handleCancel}
            className="h-9 px-3"
            aria-label={t('emailEdit.cancelButton')}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 mb-4 p-3 bg-muted/30 rounded-lg group hover:bg-muted/50 transition-colors">
      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{t('emailEdit.confirmed')}</p>
        <p className="text-sm font-medium truncate">{email}</p>
      </div>
      <button
        type="button"
        onClick={() => {
          setIsEditing(true);
          trackGTMEvent('email_edit_icon_clicked', {
            event_category: 'form_interaction',
            source
          });
          trackGTMFormEngagement(`${source}_modal`, 'edit_icon_clicked', 'email');
          trackEventMp('email_edit_icon_clicked', { source });
          trackEventHj('email_edit_icon_clicked');
          trackFormEngagementHj(`${source}_modal`, 'edit_icon_clicked');
          trackFormEngagementMp(`${source}_modal`, 'edit_icon_clicked');
        }}
        className="p-2 rounded-md hover:bg-newsletter-primary/10 text-newsletter-primary opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100"
        aria-label={t('emailEdit.editButton')}
      >
        <Pencil className="w-4 h-4" />
      </button>
    </div>
  );
};
